// ============================================
// SubscriptionPlans Component
// ============================================
// Display available subscription plans
// Allow users to select and upgrade/downgrade
// ============================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import AppFooter from '../AppFooter';
import { FaHome, FaReceipt, FaCreditCard } from 'react-icons/fa';
import './SubscriptionPlans.css';

const SubscriptionPlans = ({ tenantId, currentPlanId, onPlanChanged }) => {
  const navigate = useNavigate();
  const params = useParams();
  const actualTenantId = tenantId || params.tenant;
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/billing/plans');
      setPlans(res.data.plans);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = async (newPlanId) => {
    if (newPlanId === currentPlanId) {
      return;
    }

    const newPlan = plans.find(p => p.id === newPlanId);
    const currentPlan = plans.find(p => p.id === currentPlanId);

    const isUpgrade = newPlan.price_monthly > currentPlan.price_monthly;
    const action = isUpgrade ? 'upgrade to' : 'downgrade to';

    if (!window.confirm(`Are you sure you want to ${action} the ${newPlan.name} plan? Changes will take effect immediately and you may be charged prorated amounts.`)) {
      return;
    }

    try {
      setChanging(true);
      await axios.put(`/api/billing/subscription/${actualTenantId}/plan`, {
        newPlanId: newPlanId
      });

      alert(`Successfully ${isUpgrade ? 'upgraded' : 'downgraded'} to ${newPlan.name} plan!`);
      
      if (onPlanChanged) {
        onPlanChanged();
      }
    } catch (err) {
      console.error('Error changing plan:', err);
      alert('Failed to change plan. Please try again.');
    } finally {
      setChanging(false);
    }
  };

  if (loading) {
    return <div className="plans-loading">Loading plans...</div>;
  }

  if (error) {
    return <div className="plans-error">{error}</div>;
  }

  const formatFeatureLimit = (value, isUnlimited) => {
    if (isUnlimited) return 'Unlimited';
    return value;
  };

  return (
    <div className="subscription-plans" style={{ position: 'relative', paddingBottom: '72px' }}>
      <h2>Subscription Plans</h2>
      <p className="plans-description">
        Choose the plan that best fits your needs. You can upgrade or downgrade at any time.
      </p>

      <div className="plans-grid">
        {plans.map(plan => {
          const isCurrentPlan = plan.id === currentPlanId;
          const isUpgrade = plan.price_monthly > (plans.find(p => p.id === currentPlanId)?.price_monthly || 0);

          return (
            <div 
              key={plan.id} 
              className={`plan-card ${isCurrentPlan ? 'current-plan' : ''}`}
            >
              {isCurrentPlan && (
                <div className="current-badge">Current Plan</div>
              )}

              <div className="plan-header">
                <h3>{plan.name}</h3>
                <div className="plan-price">
                  <span className="price-amount">${plan.price_monthly}</span>
                  <span className="price-period">/month</span>
                </div>
              </div>

              <div className="plan-features">
                <div className="feature-item">
                  <svg className="feature-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>
                    {formatFeatureLimit(plan.included_users, plan.is_unlimited_users)} 
                    {plan.is_unlimited_users ? ' users' : ` user${plan.included_users !== 1 ? 's' : ''}`}
                  </span>
                </div>

                <div className="feature-item">
                  <svg className="feature-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>
                    {formatFeatureLimit(plan.included_integrations, plan.is_unlimited_integrations)} 
                    {plan.is_unlimited_integrations ? ' integrations' : ' integration' + (plan.included_integrations !== 1 ? 's' : '')}
                  </span>
                </div>

                <div className="feature-item">
                  <svg className="feature-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>
                    {formatFeatureLimit(plan.included_posts_monthly, plan.is_unlimited_posts)} 
                    {plan.is_unlimited_posts ? ' posts/month' : ` post${plan.included_posts_monthly !== 1 ? 's' : ''}/month`}
                  </span>
                </div>

                {plan.extra_user_price > 0 && !plan.is_unlimited_users && (
                  <div className="feature-item extra-pricing">
                    <svg className="feature-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                    </svg>
                    <span>${plan.extra_user_price}/month per additional user</span>
                  </div>
                )}

                <div className="feature-item trial-info-item">
                  <svg className="feature-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <span>{plan.trial_days}-day free trial</span>
                </div>
              </div>

              <div className="plan-footer">
                {!isCurrentPlan && (
                  <button
                    className={`btn-plan ${isUpgrade ? 'btn-upgrade' : 'btn-downgrade'}`}
                    onClick={() => handleChangePlan(plan.id)}
                    disabled={changing}
                  >
                    {changing ? 'Processing...' : isUpgrade ? 'Upgrade' : 'Downgrade'}
                  </button>
                )}
                {isCurrentPlan && (
                  <button className="btn-plan btn-current" disabled>
                    Current Plan
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="plans-footer">
        <p className="plans-note">
          All plans include core features. Extra users beyond the included amount are billed at
          $1/month each (except Enterprise which has unlimited users).
        </p>
      </div>

      {/* App Footer */}
      <AppFooter
        buttons={[
          {
            icon: <FaReceipt />,
            label: "Billing",
            onClick: () => navigate(`/${actualTenantId}/billing`),
          },
          {
            icon: <FaHome />,
            label: "Home",
            onClick: () => navigate(`/${actualTenantId}/dashboard`),
          },
          {
            icon: <FaCreditCard />,
            label: "Payment",
            onClick: () => navigate(`/${actualTenantId}/billing/payment-methods`),
          },
        ]}
      />
    </div>
  );
};

export default SubscriptionPlans;